'use client';

import { useEffect, useState } from 'react';
import PropertyCardEnhanced from './property-card-enhanced';
import Link from 'next/link';
import { ArrowRight, Home } from 'lucide-react';

interface Property {
  id: string;
  title: string;
  slug: string;
  projectName?: string;
  propertyType?: string;
  rentalPriceMonthly: number;
  price?: number;
  bedrooms: number;
  bathrooms: number;
  parkingSpaces: number;
  floorAreaSqm: number;
  floorNumber?: number;
  furnishing?: string;
  primaryImageUrl?: string;
  images?: string[];
  city?: string;
  availableUnits?: number;
}

export default function FeaturedResidential() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch all properties and filter residential on client side
    fetch('/api/properties?limit=50&listing_type=rent&sort_by=created_at&sort_order=asc')
      .then(res => res.json())
      .then(response => {
        console.log('Featured Residential Response:', response);
        const props = response.data?.properties || response.properties || [];

        // Filter ONLY residential property types
        const residentialProps = props.filter((prop: any) =>
          ['condo', 'house', 'house_and_lot', 'townhouse'].includes(prop.property_type)
        ).slice(0, 9); // Limit to 9 after filtering

        // Transform API response
        const transformedProps = residentialProps.map((prop: any) => ({
          id: prop.id,
          title: prop.title,
          slug: prop.slug,
          projectName: prop.projectName || prop.buildingName || prop.building_name,
          propertyType: prop.propertyType || prop.property_type,
          rentalPriceMonthly: parseFloat(prop.rentalPriceMonthly || prop.rental_price_monthly || prop.price || 0),
          price: parseFloat(prop.price || prop.rentalPriceMonthly || 0),
          bedrooms: prop.bedrooms || 0,
          bathrooms: prop.bathrooms || 0,
          parkingSpaces: prop.parkingSlots || prop.parkingSpaces || prop.parking || 0,
          floorAreaSqm: parseFloat(prop.floorAreaSqm || prop.floor_area_sqm || 0),
          floorNumber: prop.floorNumber || prop.floor_number,
          furnishing: prop.furnishing,
          primaryImageUrl: prop.primaryImageUrl || prop.primary_image_url,
          images: prop.images,
          city: prop.city,
          availableUnits: prop.availableUnits || prop.available_units
        }));

        setProperties(transformedProps);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading residential properties:', err);
        setLoading(false);
      });
  }, []);

  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 rounded-full mb-4">
            <Home className="w-4 h-4 text-cyan-600" />
            <span className="text-sm font-semibold text-cyan-600">Featured Homes</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Available Homes Right Now
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Verified units with confirmed viewing schedules, clear rental terms, and direct coordination.
          </p>
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-600 mt-4">Loading homes...</p>
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-600 text-lg">No residential properties available at the moment</p>
            <Link
              href="/properties?listing_type=rent"
              className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg font-semibold transition-all"
            >
              Browse All Properties
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {properties.map((property) => (
                <PropertyCardEnhanced
                  key={property.id}
                  property={property}
                />
              ))}
            </div>

            {/* View All Button */}
            <div className="text-center mt-12">
              <Link
                href="/properties?listing_type=rent&property_type=condo,house,townhouse"
                className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl font-bold transition-all shadow-lg hover:shadow-xl group"
              >
                View All Available Homes
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <p className="text-sm text-gray-500 mt-4">
                Or request a viewing through our residential team
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
